import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DECISION_SCHEMA_LABEL, type DecisionSchemaCode } from '@planning/contracts';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { avisar } from '@xiltrion/ui/sonner';
import { apiPost } from '@/api/client';
import { avisarError } from '@/api/hooks';
import type { DecisionRow } from '@/api/types';

/** «Es / parece / podría ser» según la confianza (patrón del portal de SGDEA). */
export function confidenceWord(c: number | null | undefined) {
  if (c == null) return 'podría ser';
  return c >= 0.9 ? 'es' : c >= 0.7 ? 'parece' : 'podría ser';
}

export function ConfidenceBadge({ decision }: { decision: Pick<DecisionRow, 'confidence' | 'reviewLevel' | 'fallbackUsed' | 'engine'> }) {
  const pct = decision.confidence != null ? Math.round(decision.confidence * 100) : null;
  const variant = decision.reviewLevel === 'AUTOMATIC' ? 'success-light' : decision.reviewLevel === 'REVIEW_RECOMMENDED' ? 'warning-light' : 'destructive-light';
  return <Badge variant={variant} title={`Motor: ${decision.engine}${decision.fallbackUsed ? ' (respaldo)' : ''}`}><Icono nombre="mdi:robot-outline" />{pct !== null ? `${pct}%` : '—'}{decision.fallbackUsed ? ' · reglas' : ''}</Badge>;
}

/** Etiqueta legible del valor decidido (las opciones vienen guardadas en la decisión). */
export function valueLabel(d: DecisionRow, field: string, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  const opt = d.reasoning?.options?.[String(value)] ?? Object.entries(d.reasoning?.options ?? {}).find(([k]) => k.endsWith(`:${String(value)}`))?.[1];
  if (opt) return opt.label;
  const f = d.reasoning?.schema?.fields.find((x) => x.name === field);
  return f?.options?.find((o) => o.value === value)?.label ?? String(value ?? '—');
}

/** Una sugerencia del motor: confirmarla, corregirla (queda como dato de evaluación) o descartarla. Nunca se aplica sola. */
export function DecisionSuggestion({ decision, onDone }: { decision: DecisionRow; onDone?: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const main = decision.decisions[0];
  const field = decision.reasoning?.schema?.fields.find((f) => f.name === main?.field);
  const [value, setValue] = useState<string | null>(main ? String(main.value) : null);
  const review = (action: 'CONFIRM' | 'OVERRIDE' | 'DISMISS') =>
    apiPost(`/decisions/${decision.id}/review`, { action, ...(action === 'OVERRIDE' && main ? { values: { [main.field]: value }, reasonCode: 'INCORRECT' } : {}) })
      .then(() => {
        avisar.success(action === 'DISMISS' ? 'Sugerencia descartada' : 'Listo');
        void qc.invalidateQueries({ queryKey: ['decisions'] });
        onDone?.();
      }, avisarError);
  if (!main) return null;
  return (
    <Alert variant="info">
      <Icono nombre="mdi:robot-outline" />
      <AlertTitle className="flex flex-wrap items-center gap-2">{DECISION_SCHEMA_LABEL[decision.schemaCode as DecisionSchemaCode] ?? decision.schemaCode}<ConfidenceBadge decision={decision} /></AlertTitle>
      <AlertDescription className="space-y-2">
        {editing && field?.options ? (
          <SelectorSimple size="sm" value={value} onChange={setValue} options={field.options} className="w-full" />
        ) : (
          <p>{decision.decisions.map((d) => `${decision.reasoning?.schema?.fields.find((f) => f.name === d.field)?.label ?? d.field}: ${confidenceWord(d.confidence ?? decision.confidence)} «${valueLabel(decision, d.field, d.value)}»`).join(' · ')}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {editing ? <Button size="sm" onClick={() => void review('OVERRIDE')}>Guardar corrección</Button> : <Button size="sm" onClick={() => void review('CONFIRM')}><Icono nombre="mdi:check" />Aplicar</Button>}
          {field?.options && !editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Corregir</Button>}
          <Button size="sm" variant="ghost" onClick={() => void review('DISMISS')}>Descartar</Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
